import { defineChain, getAddress, isAddress, type Address } from "viem";

export const hyperEvm = defineChain({
  id: 999,
  name: "HyperEVM",
  nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
  rpcUrls: {
    default: { http: [import.meta.env.VITE_RPC_URL || "https://rpc.hyperliquid.xyz/evm"] },
  },
  blockExplorers: {
    default: { name: "HyperScan", url: "https://hyperscan.com" },
  },
});

const configuredFactory = import.meta.env.VITE_FACTORY_ADDRESS as string | undefined;
export const FACTORY_ADDRESS: Address | undefined =
  configuredFactory && isAddress(configuredFactory) && BigInt(configuredFactory) !== 0n
    ? getAddress(configuredFactory)
    : undefined;

export const factoryAbi = [
  {
    type: "function",
    name: "launchCount",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "launchAt",
    stateMutability: "view",
    inputs: [{ name: "index", type: "uint256" }],
    outputs: [
      { name: "token", type: "address" },
      { name: "pool", type: "address" },
      { name: "creator", type: "address" },
      { name: "createdAt", type: "uint64" },
      { name: "metadataURI", type: "string" },
    ],
  },
  {
    type: "function",
    name: "swapGateway",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    type: "function",
    name: "LAUNCH_FEE",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "createToken",
    stateMutability: "payable",
    inputs: [
      { name: "name", type: "string" },
      { name: "symbol", type: "string" },
      { name: "metadataURI", type: "string" },
      { name: "minTokensOut", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [
      { name: "tokenAddress", type: "address" },
      { name: "poolAddress", type: "address" },
    ],
  },
] as const;

export const tokenAbi = [
  { type: "function", name: "name", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "bonded", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", name: "bondingTarget", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "quoter", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "balanceOf", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "allowance", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "approve", stateMutability: "nonpayable", inputs: [{ name: "spender", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ type: "bool" }] },
] as const;

export const gatewayAbi = [
  { type: "function", name: "bondingBalance", stateMutability: "view", inputs: [{ name: "token", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "wrappedNative", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  {
    type: "function", name: "buy", stateMutability: "payable",
    inputs: [{ name: "token", type: "address" }, { name: "minTokensOut", type: "uint256" }, { name: "recipient", type: "address" }, { name: "deadline", type: "uint256" }],
    outputs: [{ name: "tokensOut", type: "uint256" }],
  },
  {
    type: "function", name: "sell", stateMutability: "nonpayable",
    inputs: [{ name: "token", type: "address" }, { name: "tokensIn", type: "uint256" }, { name: "minNativeOut", type: "uint256" }, { name: "recipient", type: "address" }, { name: "deadline", type: "uint256" }],
    outputs: [{ name: "nativeOut", type: "uint256" }],
  },
] as const;

export const quoterV2Abi = [{
  type: "function", name: "quoteExactInputSingle", stateMutability: "nonpayable",
  inputs: [{ name: "params", type: "tuple", components: [
    { name: "tokenIn", type: "address" }, { name: "tokenOut", type: "address" },
    { name: "amountIn", type: "uint256" }, { name: "fee", type: "uint24" },
    { name: "sqrtPriceLimitX96", type: "uint160" },
  ] }],
  outputs: [{ name: "amountOut", type: "uint256" }, { name: "sqrtPriceX96After", type: "uint160" }, { name: "initializedTicksCrossed", type: "uint32" }, { name: "gasEstimate", type: "uint256" }],
}] as const;

export const quoterV1Abi = [{
  type: "function", name: "quoteExactInputSingle", stateMutability: "nonpayable",
  inputs: [{ name: "tokenIn", type: "address" }, { name: "tokenOut", type: "address" }, { name: "fee", type: "uint24" }, { name: "amountIn", type: "uint256" }, { name: "sqrtPriceLimitX96", type: "uint160" }],
  outputs: [{ name: "amountOut", type: "uint256" }],
}] as const;

export type Launch = {
  token: Address;
  pool: Address;
  creator: Address;
  createdAt: number;
  metadataURI: string;
  name: string;
  symbol: string;
  image?: string;
  description?: string;
  website?: string;
  bonded: boolean;
  bondingBalance: bigint;
  bondingTarget: bigint;
  demo?: boolean;
};

export const demoLaunches: Launch[] = [
  { token: "0x000000000000000000000000000000000000dEaD", pool: "0x0000000000000000000000000000000000000001", creator: "0x71C000000000000000000000000000000000bAbe", createdAt: Date.now() / 1000 - 84, metadataURI: "", name: "Hypejet", symbol: "JET", description: "Strap in. The culture layer of Hyperliquid is taking off.", bonded: false, bondingBalance: 31_400_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
  { token: "0x000000000000000000000000000000000000bEEF", pool: "0x0000000000000000000000000000000000000002", creator: "0x02D000000000000000000000000000000000fADe", createdAt: Date.now() / 1000 - 1280, metadataURI: "", name: "Liquid Cat", symbol: "LIQ", description: "Nine lives. One HyperEVM. Infinite liquidity.", bonded: false, bondingBalance: 22_800_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
  { token: "0x000000000000000000000000000000000000c0Fe", pool: "0x0000000000000000000000000000000000000003", creator: "0x99F000000000000000000000000000000000fUn0", createdAt: Date.now() / 1000 - 4920, metadataURI: "", name: "Big Block Energy", symbol: "BBE", description: "Small blocks for speed. Big energy forever.", bonded: true, bondingBalance: 41_000_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
  { token: "0x0000000000000000000000000000000000001000", pool: "0x0000000000000000000000000000000000000004", creator: "0xA11000000000000000000000000000000000ce00", createdAt: Date.now() / 1000 - 9300, metadataURI: "", name: "Deaux Boy", symbol: "DEAUX", description: "Do it for the culture. Do it on HyperEVM.", bonded: false, bondingBalance: 12_600_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
  { token: "0x0000000000000000000000000000000000002000", pool: "0x0000000000000000000000000000000000000005", creator: "0xb0B0000000000000000000000000000000000b0b", createdAt: Date.now() / 1000 - 14120, metadataURI: "", name: "Hyperdog", symbol: "HDOG", description: "Fast paws. Faster finality.", bonded: true, bondingBalance: 44_000_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
  { token: "0x0000000000000000000000000000000000003000", pool: "0x0000000000000000000000000000000000000006", creator: "0xcafe00000000000000000000000000000000babe", createdAt: Date.now() / 1000 - 22100, metadataURI: "", name: "Green Candle", symbol: "SEND", description: "One direction. No brakes.", bonded: false, bondingBalance: 6_900_000_000_000_000_000n, bondingTarget: 40_000_000_000_000_000_000n, demo: true },
];
